import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { profile } from "../data/profile";

const links = [
  { label: "About", id: "about" },
  { label: "Expertise", id: "expertise" },
  { label: "Experience", id: "experience" },
  { label: "Awards", id: "awards" },
  { label: "Milestones", id: "achievements" },
  { label: "Services", id: "services" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = links.map((l) => document.getElementById(l.id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-[#E2E5E1]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <motion.button
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            data-testid="nav-logo"
            onClick={() => go("hero")}
            className="flex items-center gap-3 group"
          >
            <span className="h-11 w-11 rounded-md bg-navy flex items-center justify-center p-1.5 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110 ring-1 ring-gold/30">
              <img src="/assets/vp-gold.png" alt="VP" className="w-full h-full object-contain" />
            </span>
            <span className="text-left leading-tight">
              <span className="block font-display text-lg font-bold text-navy">
                {profile.shortName}
              </span>
              <span className="block text-[0.65rem] font-plex uppercase tracking-[0.18em] text-gold">
                NABH Assessor
              </span>
            </span>
          </motion.button>

          <nav className="hidden md:flex items-center gap-5 lg:gap-7">
            {links.map((l, i) => (
              <motion.button
                key={l.id}
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -2 }}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`text-sm font-semibold relative transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:bg-gradient-to-r after:from-gold after:to-teal after:rounded-full after:transition-all after:duration-500 hover:after:w-full ${
                  active === l.id
                    ? "text-navy after:w-full"
                    : "text-navy/80 hover:text-navy after:w-0"
                }`}
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 + links.length * 0.08, type: "spring" }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              data-testid="nav-contact-btn"
              onClick={() => go("contact")}
              className="btn-shine px-5 py-2.5 rounded-md bg-navy text-bone text-sm font-medium hover:bg-teal transition-colors"
            >
              Book Appointment
            </motion.button>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-navy"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-white border-t border-[#E2E5E1] px-6 py-4 space-y-3 overflow-hidden"
        >
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`block w-full text-left py-2 font-semibold border-l-2 pl-3 transition-all ${
                active === l.id
                  ? "text-navy border-gold translate-x-1"
                  : "text-navy/80 border-transparent hover:text-navy hover:translate-x-1"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-nav-contact-btn"
            onClick={() => go("contact")}
            className="w-full px-5 py-3 rounded-md bg-navy text-bone text-sm font-medium"
          >
            Book Appointment
          </button>
        </motion.div>
      )}
    </motion.header>
  );
};
